import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import CloseIcon from "../assets/icons/CloseIcon";
import { OMIGButton } from "./omig/OMIGScreenWrapper";

function ContactVerificationModal({
  open,
  type,
  email,
  phone,
  error,
  sending,
  loading,
  onVerify,
  onOpenChange,
  sendingIcon,
  verifyingIcon,
}) {
  const [code, setCode] = useState("");
  const inputRefs = useRef([]);
  const CODE_LENGTH = 5;

  useEffect(() => {
    if (open) {
      setCode("");
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [open]);

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);

    if (pastedData) {
      setCode(pastedData);

      const focusIndex = Math.min(pastedData.length, CODE_LENGTH - 1);
      setTimeout(() => inputRefs.current[focusIndex]?.focus(), 0);
    }
  };

  const handleChange = (index, e) => {
    const value = e.target.value;
    const digit = value.replace(/\D/g, "").slice(-1);

    if (!digit && value) return;

    const currentCode = code.split("");
    while (currentCode.length < CODE_LENGTH) currentCode.push("");

    currentCode[index] = digit;
    const newCode = currentCode.join("").slice(0, CODE_LENGTH);
    setCode(newCode);

    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        const newCode = code.split("");
        newCode[index - 1] = "";
        setCode(newCode.join(""));
        inputRefs.current[index - 1]?.focus();
      } else {
        const newCode = code.split("");
        newCode[index] = "";
        setCode(newCode.join(""));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH || loading || sending) return;
    onVerify && onVerify(code);
  };

  const displayTarget =
    type === "phone" && phone ? `+1${phone}` : type === "email" ? email : "";

  const title =
    type === "phone" ? "Phone number verification" : "Email verification";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <Dialog.Title />
          <Dialog.Content className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 relative">
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </Dialog.Close>

            <h2 className="text-lg font-semibold text-center mb-2">{title}</h2>
            {sending ? (
              <div className="flex flex-col items-center justify-center mb-6">
                {sendingIcon}
                <p className="text-sm text-gray-600 mt-2">
                  Sending verification code...
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-center text-gray-600 mb-6">
                  {displayTarget ? (
                    <>
                      Enter the code sent to
                      <br />
                      <span className="font-medium break-all">
                        {displayTarget}
                      </span>
                    </>
                  ) : (
                    "Enter the verification code"
                  )}
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex justify-center gap-3">
                    {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength={1}
                        inputMode="numeric"
                        value={code[index] || ""}
                        autoComplete="one-time-code"
                        aria-label={`Digit ${index + 1}`}
                        onChange={(e) => handleChange(index, e)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        ref={(el) => (inputRefs.current[index] = el)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        className="w-10 h-10 text-center border border-gray-300 rounded-md text-lg font-medium focus:outline-none focus:ring-2 focus:ring-crimsonGlory selection:bg-transparent"
                      />
                    ))}
                  </div>

                  {error && (
                    <p className="text-sm text-red-600 text-center">{error}</p>
                  )}

                  <OMIGButton
                    type="submit"
                    disabled={code.length !== CODE_LENGTH || loading || sending}
                  >
                    {loading && verifyingIcon}
                    {loading ? "Verifying..." : "Verify"}
                  </OMIGButton>

                  <Dialog.Close asChild>
                    <OMIGButton type="button" variant="outline">
                      Cancel
                    </OMIGButton>
                  </Dialog.Close>
                </form>
              </>
            )}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default ContactVerificationModal;
